'use client';

import * as React from 'react';
import { Bell, Check, X, User, Trash2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/doi-tac-module/contexts/AuthContext';
import { useLanguage } from '@/doi-tac-module/contexts/LanguageContext';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';
import { cn } from '@/doi-tac-module/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { vi, enUS } from 'date-fns/locale';

export function NotificationDropdown() {
    const { notifications, updateNotificationStatus, markNotificationsAsRead, deleteNotification, deleteAllNotifications, role } = useAuth();
    const { language } = useLanguage();
    const router = useRouter();
    const t = agencyTranslations;
    
    const unreadCount = notifications.filter(n => !n.read).length;
    const [rejectionTarget, setRejectionTarget] = React.useState<string | null>(null);

    const handleAction = (e: React.MouseEvent, id: string, status: 'APPROVED' | 'REJECTED', reason?: string) => {
        e.stopPropagation();
        updateNotificationStatus(id, status, reason);
    };

    const handleDelete = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        deleteNotification(id);
    };

    const handleDeleteAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        deleteAllNotifications();
    };

    return (
        <Popover onOpenChange={(open) => {
            if (open) markNotificationsAsRead();
            else setRejectionTarget(null);
        }}>
            <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-10 w-10 rounded-full hover:bg-slate-100 transition-colors group">
                    <Bell className="h-6 w-6 text-slate-600 group-hover:scale-110 transition-transform" />
                    {unreadCount > 0 && (
                        <span className="absolute top-2 right-2 h-4 w-4 flex items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white border-2 border-background">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 sm:w-96 p-0 shadow-2xl border-slate-200 rounded-xl overflow-hidden" align="end" sideOffset={8}>
                <div className="flex items-center justify-between p-4 bg-white border-b border-slate-100">
                    <h3 className="font-bold text-xl text-slate-900">{t.header.notifications[language]}</h3>
                    <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="text-xs font-bold text-[#0D8DC8] hover:text-[#0D8DC8]/80 hover:bg-blue-50" onClick={markNotificationsAsRead}>
                            {t.header.markAllRead[language]}
                        </Button>
                        {notifications.length > 0 && (
                            <Button variant="ghost" size="sm" className="text-xs font-bold text-red-500 hover:text-red-600 hover:bg-red-50 px-2" onClick={handleDeleteAll}>
                                {t.header.deleteAll[language]}
                            </Button>
                        )}
                    </div>
                </div>
                <div className="max-h-[480px] overflow-y-auto bg-white">
                    {notifications.length === 0 ? (
                        <div className="py-12 px-8 text-center space-y-2">
                            <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Bell className="h-8 w-8 text-slate-300" />
                            </div>
                            <p className="text-slate-500 font-medium">
                                {t.header.noNotifications[language]}
                            </p>
                        </div>
                    ) : (
                        notifications.map((notif) => (
                            <div key={notif.id} 
                                onClick={() => {
                                    if (notif.type === 'REGISTRATION_REQUEST') {
                                        router.push(`/cac-dai-ly/${notif.senderUid}?notifId=${notif.id}`);
                                    } else if (notif.type === 'CANDIDATE_CONNECT_REQUEST') {
                                        router.push(`/doi-tac/ung-vien/${notif.senderUid}?notifId=${notif.id}`);
                                    } else if (notif.type === 'AGENT_STATUS_UPDATE') {
                                        router.push(`/doi-tac/ho-so`);
                                    } else if (notif.type === 'DOCUMENT_SHARED') {
                                        router.push(`/doi-tac/tai-lieu-chia-se`);
                                    }
                                }}
                                className={cn(
                                    "p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors cursor-pointer relative group/item",
                                    !notif.read && "bg-blue-50/40"
                                )}
                            >
                                <div className="flex gap-4">
                                    <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 border border-blue-50">
                                        <User className="h-6 w-6 text-[#0D8DC8]" />
                                    </div>
                                    <div className="flex-grow space-y-1">
                                        <div className="flex justify-between items-start">
                                            <p className="text-sm leading-snug pr-6 text-slate-800">
                                                <span className="font-bold text-slate-900">{notif.senderName}</span>
                                                {notif.type === 'REGISTRATION_REQUEST' && notif.senderCompanyName && (
                                                    <span className="italic text-slate-500 font-normal"> {notif.senderCompanyName}</span>
                                                )}
                                                {' '}
                                                {notif.type === 'REGISTRATION_REQUEST' && t.masterAgentDialog.newRequest[language]}
                                                {notif.type === 'CANDIDATE_CONNECT_REQUEST' && t.masterAgentDialog.candidateConnect[language]}
                                                {notif.type === 'AGENT_STATUS_UPDATE' && (notif.status === 'APPROVED' ? t.masterAgentDialog.approvedByMaster[language] : t.masterAgentDialog.rejectedByMaster[language])}
                                                {notif.type === 'CANDIDATE_CONNECT_RESULT' && (notif.status === 'APPROVED' ? t.masterAgentDialog.approvedByAgent[language] : t.masterAgentDialog.rejectedByAgent[language])}
                                                {notif.type === 'DOCUMENT_SHARED' && (language === 'vi' ? ' đã chia sẻ tài liệu với bạn' : ' আপনার সাথে একটি নথি শেয়ার করেছেন')}
                                            </p>
                                            <div className="flex flex-col items-end gap-2 shrink-0">
                                                {!notif.read && (
                                                    <div className="h-2.5 w-2.5 bg-blue-500 rounded-full mt-1" />
                                                )}
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    className="h-7 w-7 rounded-full opacity-0 group-hover/item:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-500 -mr-1"
                                                    onClick={(e) => handleDelete(e, notif.id)}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                        <p className="text-xs font-medium text-slate-500">
                                            {formatDistanceToNow(notif.timestamp, { 
                                                addSuffix: true, 
                                                locale: language === 'vi' ? vi : enUS 
                                            })}
                                        </p>
                                        
                                        {notif.status === 'PENDING' && (
                                            <>
                                                {rejectionTarget === notif.id ? (
                                                    <div className="pt-3 space-y-2 border-t border-slate-100 mt-2">
                                                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t.header.rejectionReasons.title[language]}</p>
                                                        <div className="grid grid-cols-1 gap-1">
                                                            {Object.entries(t.header.rejectionReasons)
                                                              .filter(([key]) => key !== 'title')
                                                              .map(([key, value]) => (
                                                                <Button 
                                                                    key={key}
                                                                    variant="ghost" 
                                                                    size="sm" 
                                                                    className="justify-start text-left text-[11px] h-7 px-2 hover:bg-red-50 hover:text-red-600 rounded-md"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        const reasonText = (value as any)[language];
                                                                        handleAction(e, notif.id, 'REJECTED', reasonText);
                                                                        setRejectionTarget(null);
                                                                    }}
                                                                >
                                                                    {(value as any)[language]}
                                                                </Button>
                                                            ))}
                                                        </div>
                                                        <Button 
                                                            variant="ghost" 
                                                            size="sm" 
                                                            className="w-full text-[10px] h-6 text-slate-400 hover:text-slate-600"
                                                            onClick={(e) => { e.stopPropagation(); setRejectionTarget(null); }}
                                                        >
                                                            {language === 'vi' ? 'Hủy' : 'বাতিল করুন'}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <div className="flex gap-2 pt-3">
                                                        <Button 
                                                            size="sm" 
                                                            className="flex-grow bg-[#0D8DC8] hover:bg-[#0D8DC8]/90 text-white font-bold h-9 rounded-lg shadow-sm"
                                                            onClick={(e) => handleAction(e, notif.id, 'APPROVED')}
                                                        >
                                                            {t.masterAgentDialog.confirm[language]}
                                                        </Button>
                                                        <Button 
                                                            size="sm" 
                                                            variant="secondary"
                                                            className="flex-grow bg-slate-200 hover:bg-slate-300 text-slate-700 font-bold h-9 rounded-lg"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (notif.type === 'CANDIDATE_CONNECT_REQUEST') {
                                                                    setRejectionTarget(notif.id);
                                                                } else {
                                                                    handleAction(e, notif.id, 'REJECTED');
                                                                }
                                                            }}
                                                        >
                                                            {t.masterAgentDialog.delete[language]}
                                                        </Button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                        
                                        {notif.status === 'APPROVED' && (
                                            <div className="mt-2 text-xs font-bold text-green-600 bg-green-50 w-fit px-2 py-1 rounded-md flex items-center gap-1.5">
                                                <Check className="h-3.5 w-3.5" /> 
                                                <span>{t.masterAgentDialog.approvedLabel[language]}</span>
                                            </div>
                                        )}
                                        {notif.status === 'REJECTED' && (
                                            <div className="mt-2 space-y-1">
                                                <div className="text-xs font-bold text-red-600 bg-red-50 w-fit px-2 py-1 rounded-md flex items-center gap-1.5">
                                                    <X className="h-3.5 w-3.5" /> 
                                                    <span>{t.masterAgentDialog.rejectedLabel[language]}</span>
                                                </div>
                                                {notif.rejectionReason && (
                                                    <p className="text-[11px] text-slate-500 italic pl-1">
                                                       {t.masterAgentDialog.reasonLabel[language]}: {notif.rejectionReason}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                {notifications.length > 0 && (
                    <div className="p-3 bg-slate-50 border-t border-slate-100 text-center">
                        <p className="text-[11px] text-slate-400 font-medium">{t.header.partnerSystemFooter[language]}</p>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
